import React, { useState, useEffect, useRef } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import RequirementsTable from './components/RequirementsTable';
import RequirementsFilters from './components/RequirementsFilters';
import RequirementAnalytics from './components/RequirementAnalytics';
import RequirementDetailModal from './components/RequirementDetailModal';
import CreateRequirementModal from './components/CreateRequirementModal';
import MatchingEnginePanel from './components/MatchingEnginePanel';
import clientRequirementsService from '../../services/clientRequirementsService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const ClientRequirementsMatching = () => {
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [selectedRequirements, setSelectedRequirements] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [showMatchingPanel, setShowMatchingPanel] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  // Export / Import state
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const importFileRef = useRef(null);

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    client: '',
    skills: [],
    location: '',
    deadlineFrom: '',
    deadlineTo: ''
  });

  const { userProfile } = useAuth();

  // Mock user data for header
  const user = {
    name: userProfile?.full_name || "HR Manager",
    email: userProfile?.email || "hr@facilitypro.com",
    role: userProfile?.role || "HR Admin"
  };

  // Mock notifications
  const notifications = [
    {
      id: 1,
      title: "Urgent Requirement",
      message: "TechCorp needs 3 React developers by Aug 5",
      time: "10 minutes ago",
      type: "error",
      read: false
    },
    {
      id: 2,
      title: "New Match Found",
      message: "85% compatibility match for Production Supervisor role",
      time: "30 minutes ago",
      type: "success",
      read: false
    },
    {
      id: 3,
      title: "Client Feedback",
      message: "Healthcare Plus approved candidate for Nurse position",
      time: "1 hour ago",
      type: "info",
      read: false
    }
  ];

  // Load requirements data
  const loadRequirements = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await clientRequirementsService.getRequirements(filters);
      
      if (result.success) {
        setRequirements(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load client requirements');
    } finally {
      setLoading(false);
    }
  };

  // Load analytics data
  const loadAnalytics = async () => {
    try {
      const result = await clientRequirementsService.getRequirementsAnalytics();
      
      if (result.success) {
        setAnalytics(result.data);
      }
    } catch (err) {
      console.log('Failed to load analytics:', err);
    }
  };

  useEffect(() => {
    loadRequirements();
    loadAnalytics();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadRequirements();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleRequirementClick = (requirement) => {
    setSelectedRequirement(requirement);
  };

  const handleCloseModal = () => {
    setSelectedRequirement(null);
  };

  const handleCreateRequirement = () => {
    setEditingRequirement(null);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setEditingRequirement(null);
  };

  const handleRequirementCreated = () => {
    loadRequirements();
    loadAnalytics();
    setShowCreateModal(false);
    setEditingRequirement(null);
  };

  const handleEditRequirement = (requirement) => {
    setEditingRequirement(requirement);
    setShowCreateModal(true);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSelectionChange = (selectedIds) => {
    setSelectedRequirements(selectedIds);
  };

  const handleBulkAction = async (action) => {
    if (selectedRequirements.length === 0) return;

    try {
      let updateData = {};
      
      switch (action) {
        case 'mark_urgent':
          updateData = { priority: 'urgent' };
          break;
        case 'mark_on_hold':
          updateData = { status: 'on_hold' };
          break;
        case 'mark_completed':
          updateData = { status: 'completed' };
          break;
        default:
          return;
      }

      const result = await clientRequirementsService.bulkUpdateRequirements(
        selectedRequirements, 
        updateData
      );

      if (result.success) {
        loadRequirements();
        setSelectedRequirements([]);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to perform bulk action');
    }
  };

  const handleShowMatching = (requirement) => {
    setSelectedRequirement(requirement);
    setShowMatchingPanel(true);
  };

  const handleCloseMatching = () => {
    setShowMatchingPanel(false);
    setSelectedRequirement(null);
  };

  // Filter requirements to get counts for different statuses
  const getStatusCounts = () => {
    if (!requirements.length) return { open: 0, urgent: 0, completed: 0 };
    
    return {
      open: requirements.filter(r => r.status === 'open').length,
      urgent: requirements.filter(r => r.priority === 'urgent').length,
      completed: requirements.filter(r => r.status === 'completed').length
    };
  };

  const statusCounts = getStatusCounts();

  // Export handler
  const handleExport = async () => {
    try {
      setExporting(true);
      const result = await clientRequirementsService.exportRequirements(filters);
      if (!result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Import handlers
  const handleImportClick = () => {
    setShowImportModal(true);
    setImportResult(null);
    setImportFile(null);
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      setError('Please select a CSV file to import');
      return;
    }
    try {
      setImporting(true);
      const result = await clientRequirementsService.importRequirements(importFile);
      setImportResult(result);
      if (result.success) {
        setTimeout(() => {
          setShowImportModal(false);
          loadRequirements();
          loadAnalytics();
        }, 3000);
      }
    } catch (err) {
      setImportResult({ success: false, error: 'Failed to import requirements' });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const result = await clientRequirementsService.downloadImportTemplate();
      if (!result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        notifications={notifications}
      />
      
      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Client Requirements & Matching
              </h1>
              <p className="text-muted-foreground">
                Manage technical staff placement requests and match qualified candidates to client needs
              </p>
            </div>
            <div className="flex items-center gap-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? 'Exporting...' : 'Export Data'}
              </Button>
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                onClick={handleImportClick}
              >
                Import Requirements
              </Button>
              <Button 
                iconName="Plus" 
                iconPosition="left"
                onClick={handleCreateRequirement}
              >
                New Requirement
              </Button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Left Sidebar - Analytics */}
            <div className="xl:col-span-1">
              <RequirementAnalytics 
                analytics={analytics}
                statusCounts={statusCounts}
                loading={loading}
              />
            </div>

            {/* Main Content Area */}
            <div className="xl:col-span-3 space-y-6">
              {/* Filters */}
              <RequirementsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                resultCount={requirements?.length || 0}
              />

              {/* Bulk Actions */}
              {selectedRequirements.length > 0 && (
                <div className="bg-card border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {selectedRequirements.length} requirement(s) selected
                    </span>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleBulkAction('mark_urgent')}
                      >
                        Mark Urgent
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleBulkAction('mark_on_hold')}
                      >
                        Put On Hold
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleBulkAction('mark_completed')}
                      >
                        Mark Completed
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Requirements Table */}
              <RequirementsTable
                requirements={requirements}
                loading={loading}
                onRequirementClick={handleRequirementClick}
                onShowMatching={handleShowMatching}
                selectedRequirements={selectedRequirements}
                onSelectionChange={handleSelectionChange}
              />

              {/* Pagination */}
              {requirements.length > 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {requirements.length} requirements
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" iconName="ChevronLeft">
                      Previous
                    </Button>
                    <Button variant="outline" size="sm">
                      1
                    </Button>
                    <Button variant="outline" size="sm" iconName="ChevronRight">
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <RequirementDetailModal
        requirement={selectedRequirement}
        isOpen={!!selectedRequirement && !showMatchingPanel}
        onClose={handleCloseModal}
        onRequirementUpdated={(updatedRequirement) => {
          setSelectedRequirement(updatedRequirement);
          loadRequirements();
        }}
        onEditRequirement={handleEditRequirement}
        onShowMatching={handleShowMatching}
      />

      <CreateRequirementModal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        onRequirementCreated={handleRequirementCreated}
        initialData={editingRequirement}
        requirementId={editingRequirement?.id || null}
      />

      <MatchingEnginePanel
        requirement={selectedRequirement}
        isOpen={showMatchingPanel}
        onClose={handleCloseMatching}
      />

      {/* Import Requirements Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowImportModal(false)}>
          <div className="bg-background rounded-lg p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Import Requirements</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowImportModal(false)}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-sm text-blue-800">
              <p className="font-medium mb-1">How to import:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Download the CSV template below</li>
                <li>Fill in your requirements (Client ID is required — use IDs from Client Management)</li>
                <li>Upload the completed CSV file</li>
              </ol>
            </div>

            {/* Download template */}
            <div className="mb-4">
              <Button
                variant="outline"
                size="sm"
                iconName="FileText"
                iconPosition="left"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
                className="w-full"
              >
                {downloadingTemplate ? 'Downloading...' : 'Download CSV Template'}
              </Button>
            </div>

            {/* File upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Select CSV File *
              </label>
              <input
                ref={importFileRef}
                type="file"
                accept=".csv"
                onChange={handleImportFileChange}
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
              />
              {importFile && (
                <p className="text-xs text-muted-foreground mt-1">
                  Selected: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>

            {/* Import result */}
            {importResult && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                importResult.success
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                {importResult.success ? (
                  <div>
                    <p className="font-medium mb-1">
                      ✓ {importResult.data?.imported || 0} requirement(s) imported successfully
                    </p>
                    {importResult.data?.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium text-yellow-800">Warnings ({importResult.data.errors.length}):</p>
                        <ul className="list-disc list-inside mt-1 space-y-0.5 text-yellow-700 max-h-32 overflow-y-auto">
                          {importResult.data.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="text-xs text-green-600 mt-2">Refreshing list in 3 seconds…</p>
                  </div>
                ) : (
                  <p>{importResult.error || 'Import failed'}</p>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-end pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setShowImportModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleImportSubmit}
                disabled={!importFile || importing}
                iconName={importing ? 'Loader' : 'Upload'}
                iconPosition="left"
              >
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientRequirementsMatching;